import { Checkbox, Dropdown, Input, Option, TableRow } from '@sam/components';
import { AgendaEntry, AgendaEntryGroup } from '@sam/components/src/Agenda/Agenda.types';
import { Dispatch, SetStateAction } from 'react';
import { NavigateFunction } from 'react-router-dom';
import {
  AbsenceReason,
  AdjustmentType,
  AzkReduction,
  AzkReductionType,
  Company,
  Contact,
  County,
  CustomerUser,
  DayOfWeek,
  DriverLicenseType,
  EducationLevel,
  EducationQualification,
  EmploymentHistoryEntry,
  FamilyState,
  Gender,
  MonthlyOvertime,
  Office,
  PdlUser,
  Project,
  RequiredState,
  SchambeckUser,
  Schedule,
  SimpleUser,
  TaxClass,
  UserFunction,
  VacationChange,
  WorkQualification,
  WorkSchedule,
  WorkScheduleDistribution,
  YearlyTimeOverview,
  generateDropdownOptions,
  generateEmptyContractInformation,
  generateIsoDateForDateAndTime,
} from 'shared';
import { Language } from 'shared/src/Language/Language.types';
import dayjs from 'shared/src/tools/Dayjs';
import { Right, UserRight, UserRole } from 'shared/src/userRole/UserRole.types';
import { uid } from 'uid';

import i18n from '../../i18n/i18n';
import { generateEmptyBankAccount } from '../office/Office.utils';
import {
  calculateDailyWorkForGivenDays,
  calculateDailyWorkTimeForWeeklySchedule,
  calculatePreciseWorkScheduleForUser,
  getTotalWorkingTimeFromPreciseMapping,
} from '../timeTracking/TimeTracking.utils';
import { IconComponent, IconType } from '../../components/icons/IconComponent';

/**
 * Helper to transform the given users into table row entries to display
 *
 * @param usersRights decides if the edit and delete boxes are shown
 * @param users list of users to display
 * @param functions userFunctions to display the names
 * @param offices to display the names
 * @param navigate handles the edit redirect
 * @param setUserToDeleteId selectes an entry to delete
 * @param qualifications to display the names
 * @returns List of table rows to display
 * @tested
 */
export const convertUsersIntoTableEntries = (
  userRights: UserRight,
  users: SchambeckUser[],
  functions: UserFunction[],
  offices: Office[],
  navigate: NavigateFunction,
  setUserToDeleteId: (userToDelete: string) => void,
  qualifications: WorkQualification[],
): TableRow[] => {
  return users
    .sort((userA, userB) => userA.lastName.localeCompare(userB.lastName))
    .map((user) => {
      const userFunction: UserFunction | undefined = functions.find(
        (userFunction) => userFunction.id === user.userFunctionId,
      );
      const userOffice: Office | undefined = offices.find(
        (office) => office.id === user.contractInformation.officeId,
      );

      const currentUserOffice: Office | undefined = offices.find(
        (office) => office.id === user.contractInformation.currentOfficeId,
      );
      const userQualifications: WorkQualification[] = qualifications.filter((qualification) =>
        user.workQualifications.includes(qualification.id),
      );
      return {
        id: user.id!,
        onClick: () => navigate(`/user/create`, { state: { user } }),
        content: [
          user.lastName,
          user.firstName,
          i18n.t(
            `pages.user.create.contract.employmentType.${user.contractInformation.employmentType}`,
          ),
          userFunction?.title || '-',
          userOffice?.name || '-',
          currentUserOffice?.name || '-',

          user.contact.phone +
            (user.contact.phone ? ', ' : '') +
            (user.contact.phonePrivate && user.contact.phonePrivate !== '' && '(P)') +
            user.contact.phonePrivate +
            (user.contact.phonePrivate ? ', ' : '') +
            user.contact.mobile +
            (user.contact.mobile ? ', ' : '') +
            (user.contact.mobilePrivate && user.contact.mobilePrivate !== '' && '(P)') +
            user.contact?.mobilePrivate || '-',
          userQualifications
            .map(
              (quali, index) => quali.name + (index < user.workQualifications.length ? ', ' : ''),
            )
            .toString(),
          user.disabled
            ? i18n.t('pages.user.overview.deactivated')
            : i18n.t('pages.user.overview.active'),
          <div className="table-action">
            {isUserAllowedToDo(userRights, [Right.USER_EDIT, Right.USER_VIEW_ALL]) && (
              <IconComponent
                type={IconType.EDIT}
                height={40}
                width={40}
                onClick={(evt) => {
                  evt.stopPropagation();
                  navigate(`/user/create`, { state: { user } });
                }}
              />
            )}
            {isUserAllowedToDo(userRights, Right.USER_DELETE) && (
              <IconComponent
                type={IconType.DELETE}
                height={40}
                width={40}
                onClick={(evt) => {
                  evt.stopPropagation();
                  setUserToDeleteId(user.id);
                }}
              />
            )}
          </div>,
        ],
      };
    });
};

/**
 * Helper method to convert UserRoles into TableEntries
 * @param userRoles to convert
 * @param navigate NavigateFunction to edit the roles
 * @returns Array of TableRows
 */
export const convertUserRolesIntoTableEntries = (
  userRoles: UserRole[],
  navigate: NavigateFunction,
  setRoleToUpdate: Dispatch<SetStateAction<string | undefined>>,
): TableRow[] => {
  return userRoles.map((role) => ({
    id: role.id,
    onClick: () => navigate('/roles/edit', { state: { historyRole: role } }),
    content: [
      role.name,
      role.disabled
        ? i18n.t('pages.userRole.overview.deactivated')
        : i18n.t('pages.userRole.overview.active'),
      <div className="table-action__icon-wrapper">
        <IconComponent
          type={IconType.EDIT}
          onClick={(evt) => {
            evt.stopPropagation();
            navigate('/roles/edit', { state: { historyRole: role } });
          }}
        />
        <IconComponent
          type={IconType.UPDATE}
          onClick={(evt) => {
            evt.stopPropagation();
            setRoleToUpdate(role.id);
          }}
        />
      </div>,
    ],
  }));
};

/**
 * Helper to generate empty user Object
 * @returns
 */
export const generateEmptySchambeckUser = (createdBy: string): SchambeckUser => ({
  id: undefined!,
  password: '',
  createDate: new Date(),
  createdBy: createdBy,
  lastUpdated: new Date(),
  updatedBy: '',
  gender: Gender.MALE,
  title: '',
  vacationChanges: [],
  userLanguages: [],
  firstName: '',
  lastName: '',
  username: '',
  contact: generateEmptyContactInformation(),
  businessAreaIds: [],
  userFunctionId: '',
  privateInformation: {
    id: uid(),
    birthDate: new Date(),
    residencePermitEndDate: new Date(),
    workPermitEndDate: new Date(),
    birthCity: '',
    nationality: '',
    bankAccount: generateEmptyBankAccount(),
    children: false,
    healthInsurance: '',
    passportNumber: '',
    religion: '',
    taxId: '',
    driverLicenseCopyState: RequiredState.UNKNOWN,
    driverLicenseType: DriverLicenseType.UNKNOWN,
    educationLevel: EducationLevel.UNKNOWN,
    educationQualification: EducationQualification.UNKNOWN,
    familyState: FamilyState.LEDIG,
    taxClass: TaxClass.III,
    equalized: false,
    severlyDisabled: false,
  },
  contractInformation: generateEmptyContractInformation(createdBy),
  personalNumber: '',
  disabled: false,
  deputyId: '',
  supervisorId: '',
  officeId: '',
  right: { customRights: new Map(), role: generateEmptyUserRole(createdBy) },
  workQualifications: [],
  customerTimes: new Map<string, number>(),
  availableOffices: [],
  availableVacationDays: 0,
  language: Language.DE,
});

/**
 * Helper to create a new and initialized instance of a UserFunction
 *
 * @param override Optional parameter to overide
 * @returns The newly created instance
 */
export const generateEmptyUserFunction = (override?: Partial<UserFunction>): UserFunction => ({
  id: undefined!,
  createdBy: undefined!,
  createDate: undefined!,
  title: '',
  names: new Map<Language, string>(),
  disabled: false,
  ...override,
});

/**
 * Helper method to create an empty UserRole
 * @param userId that generated the role
 * @returns empty UserRole
 */
export const generateEmptyUserRole = (userId: string): UserRole => ({
  createDate: new Date(),
  createdBy: userId,
  disabled: false,
  id: undefined!,
  lastUpdate: new Date(),
  name: '',
  rights: [Right.NONE],
});

/**
 * Helper to generate empty contact information
 * @returns empty contact information
 */
export const generateEmptyContactInformation = (): Contact => ({
  id: uid(),
  phone: '',
  mobile: '',
  city: '',
  countryCode: 'DE',
  mail: '',
  street: '',
  streetNumber: '',
  zip: '',
  mailPrivate: '',
  phonePrivate: '',
  mobilePrivate: '',
  fax: '',
  cityPrivate: '',
  countryCodePrivate: 'DE',
  countyPrivate: County.BAVARIA,
  streetNumberPrivate: '',
  streetPrivate: '',
  website: '',
  websitePrivate: '',
  zipPrivate: '',
  county: County.BAVARIA,
});
/**
 * Helper method to get all subordinates for a given user id
 * @param users array of users to search in
 * @param supervisorId if of the user to search for
 * @returns array of subordinate users
 */
export const getSubordinatesForUser = (
  users: SchambeckUser[],
  supervisorId: string,
): SchambeckUser[] => users.filter((user) => user.supervisorId === supervisorId);

/**
 * Util to update a user object with the given worktime schedule. Also calculates
 * the weekly total working time.
 *
 * @param user The user to update
 * @param selectedDays The selected days in case the user has daily based schedules
 * @param preciseEntries The precise mapping in case the user has a precise schedule
 * @returns The updated user object
 * @tested
 */
export const generateWorkTimeSchedulesForUser = (
  user: SchambeckUser,
  selectedDays: DayOfWeek[] = [],
  preciseEntries: Map<DayOfWeek, number> = new Map<DayOfWeek, number>(),
): SchambeckUser => {
  if (
    !user.contractInformation ||
    (user.contractInformation?.workScheduleDistribution !== WorkScheduleDistribution.PRECISE &&
      user.contractInformation?.weeklyWorkingTime <= 0)
  )
    return user;
  let createdSchedules: WorkSchedule[];
  let weeklyWorkingTime: number = user.contractInformation.weeklyWorkingTime;
  switch (user.contractInformation.workScheduleDistribution) {
    case WorkScheduleDistribution.WEEKLY:
      createdSchedules = calculateDailyWorkTimeForWeeklySchedule(
        user.contractInformation.weeklyWorkingTime || 0,
      );
      break;
    case WorkScheduleDistribution.DAILY:
      createdSchedules = calculateDailyWorkForGivenDays(
        user.contractInformation.weeklyWorkingTime,
        selectedDays,
      );
      break;
    case WorkScheduleDistribution.PRECISE:
      createdSchedules = calculatePreciseWorkScheduleForUser(preciseEntries);
      weeklyWorkingTime = getTotalWorkingTimeFromPreciseMapping(user, createdSchedules);
      break;
  }
  return {
    ...user,
    contractInformation: {
      ...user.contractInformation,
      weeklyWorkingTime,
      workSchedules: createdSchedules,
    },
  };
};

/**
 * Helper method to decide if a user has the needed right to do a action
 * @param userRights the rights of the user
 * @param right the right that is necessary for the action
 * @returns boolean if id the user is allowed to to a action or not
 */
export const isUserAllowedToDo = (userRights: UserRight, rights: Right | Right[]): boolean => {
  if (userRights.customRights.get(Right.ALL)) return true;
  else if (userRights.customRights.get(Right.NONE)) return false;
  if (rights instanceof Array) {
    return rights.some(
      (right) =>
        userRights.customRights.get(right) ||
        userRights.role.rights.includes(right) ||
        userRights.role.rights.includes(Right.ALL),
    );
  } else {
    if (userRights.customRights.get(rights)) return true;
    else if (userRights.role.rights.includes(rights)) return true;
    else if (userRights.role.rights.includes(Right.ALL)) return true;
    return false;
  }
};

/**
 * Helper method to toggle a right on a userRole
 * If the role is already present in the role, it is filtered, otherwise it get´s added to the role
 * @param role to update
 * @param right that should be added or removed
 * @returns UserRole with the updated right
 */
export const updateRoleRights = (role: UserRole, right: Right): UserRole => {
  const newRights = role.rights.includes(right)
    ? role.rights.filter((existingRight) => existingRight !== right)
    : [...role.rights, right];
  return { ...role, rights: newRights };
};

/**
 * util method to get the name of a user out of simpleusers by the id
 * @param userId to get the name for
 * @param simpleUsers to search in
 * @returns name of the user or empty string if no user could be found for the id
 */
export const getUserNameForSimpleUser = (
  userId?: string,
  simpleUsers?: SimpleUser[] | SchambeckUser[] | CustomerUser[],
  reverted?: boolean,
): string => {
  if (!userId || !simpleUsers || simpleUsers.length < 1) return '-';

  const user: SimpleUser | CustomerUser | undefined = simpleUsers.find(
    (user) => user.id === userId,
  );
  if (!user) return '-';
  if (reverted) return `${user.firstName} ${user.lastName}`;
  return `${user.lastName}, ${user.firstName}`;
};

/**
 * util method to get the name of a user with the default format
 * @param user to get the name for
 * @returns name of the user as lastname, firstname
 */
export const getUserName = (user: SchambeckUser | SimpleUser | CustomerUser | PdlUser): string => {
  return `${user.lastName}, ${user.firstName}`;
};

/**
 * Helper to transform the given simpleusers into dropdown options
 *
 * @param allSimpleUser The list of simple users to display
 * @returns Entries for a dropdown with all simple users as Lastname, Firstname
 */
export const generateDropdownOptionsForSimpleUser = (allSimpleUser: SimpleUser[]): Option[] => {
  return allSimpleUser
    .map((user) => ({
      label: getUserName(user),
      value: user.id,
    }))
    .sort((entry1, entry2) => entry1.label.localeCompare(entry2.label));
};

/**
 * Util method to convert user functions into tableRows
 * @param entries to convert
 * @param navigate navigateFunction to handle edit click
 * @returns Array of TableRows
 */
export const convertUserFunctionsIntoTableEntries = (
  entries: UserFunction[],
  navigate: NavigateFunction,
): TableRow[] => {
  return entries
    .filter((userFunction) => !userFunction.executiveUserId)
    .map((entry) => ({
      id: entry.id,
      onClick: () => navigate('edit', { state: { entry } }),
      content: [
        entry.title,
        new Date(entry.createDate).toLocaleDateString('de-DE') || '-',
        entry.lastUpdate ? new Date(entry.lastUpdate).toLocaleDateString('de-DE') : '-',
        <IconComponent
          type={IconType.EDIT}
          width={30}
          onClick={(evt) => {
            evt.stopPropagation();
            navigate('edit', { state: { entry } });
          }}
        />,
      ],
    }));
};

/**
 * Util method to convert users into TableEntries including a checkbox to select them
 * @param users list of users to display
 * @param setSelectedUsers updateMethod to select users
 * @param selectedUsers users where the checkbox is checked
 * @returns Array of TableRows
 */
export const convertUsersIntoSelectableTableRows = (
  users: SimpleUser[],
  setSelectedUsers: Dispatch<SetStateAction<string[]>>,
  selectedUsers: string[],
): TableRow[] => {
  return users.map((loadedUser) => ({
    id: loadedUser.id,
    content: [
      <Checkbox
        isChecked={selectedUsers.includes(loadedUser.id)}
        onCheck={() =>
          setSelectedUsers((prev) =>
            prev.includes(loadedUser.id)
              ? prev.filter((prevUser) => prevUser !== loadedUser.id)
              : [...selectedUsers, loadedUser.id],
          )
        }
      />,
      getUserName(loadedUser),
    ],
  }));
};

/**
 * Util to convert the given schedules into a form so that an Agenda can display it
 *
 * @param schedules All schedule entries to display
 * @param userId The id of the corresponding user
 * @param allProjects List of all projects
 * @param allAbsenceReasons All configured absence reasons
 * @returns The Schedules as agenda entry
 */
export const generateUserAgendaScheduleEntries = (
  schedules: Schedule[],
  userId: string,
  allProjects: Project[],
  allAbsenceReasons: AbsenceReason[],
): AgendaEntryGroup[] => {
  const entries: AgendaEntry[] = schedules.map((schedule) => ({
    id: userId + '/' + schedule.id,
    title:
      schedule.appointmentDetail?.title ||
      allProjects.find((project) => project.id === schedule.referenceId)?.numberRangeNumber ||
      i18n.t('pages.user.create.schedule.projectPlaceholder'),
    background: schedule.appointmentDetail
      ? allAbsenceReasons.find((reason) => reason.id === schedule.appointmentDetail?.reasonId)
          ?.color || 'lightcyan'
      : '#D3F4E4',
    start: generateIsoDateForDateAndTime(
      schedule.scheduleDate || schedule.startDate!,
      schedule.appointmentDetail?.startTime || 0,
    ),
    end: generateIsoDateForDateAndTime(
      schedule.scheduleDate || schedule.endDate!,
      schedule.appointmentDetail?.endTime || 140,
    ),
    onClick: () => {},
  }));
  return [{ id: userId, entries, groupName: '' }];
};

/**
 * Util method to generate the Agenda Entries for the PlanningBoard
 * @param schedules to display
 * @param users to display
 * @param allProjects list of all projects to display the projectSchedules
 * @param allAbsenceReasons All possible absenceReasons
 * @returns  Array containing the AgendaEntryGroups
 */
export const generatePlanningBoardEntries = (
  schedules: Schedule[],
  users: SimpleUser[],
  allProjects: Project[],
  allAbsenceReasons: AbsenceReason[],
): AgendaEntryGroup[] => {
  const entries: AgendaEntryGroup[] = [];
  [...users].forEach((user) => {
    const schedulesForUser: Schedule[] = schedules?.filter((schedule) => {
      return (
        schedule.referenceId === user.id ||
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        Array.from(schedule.scheduledUsers).some(([_, plannedUsers]) =>
          plannedUsers?.some((id) => id === user.id),
        )
      );
    });

    entries.push({
      groupName: getUserNameForSimpleUser(user.id, users),
      entries: generateUserAgendaScheduleEntries(
        schedulesForUser,
        user.id,
        allProjects,
        allAbsenceReasons,
      )[0].entries,
      id: user.id,
    });
  });
  return entries;
};

/**
 * Util method to combine multiple agenda Entry groups by their id
 * @param entriesOne Array of entryGroups
 * @param entriesTwo Other array to combine
 * @returns Array containing the combined agendaGroups
 * //TODO TEST ME
 */
export const combineAgendaEntries = (
  entriesOne: AgendaEntryGroup[],
  entriesTwo: AgendaEntryGroup[],
): AgendaEntryGroup[] => {
  const allEntries: AgendaEntryGroup[] = [...entriesOne, ...entriesTwo];

  const combinedEntries: AgendaEntryGroup[] = [];
  allEntries.forEach((entry) => {
    const existingEntry: AgendaEntryGroup | undefined = combinedEntries.find(
      (resultEntry) => resultEntry.id === entry.id,
    );

    if (existingEntry) {
      existingEntry.entries = [...existingEntry.entries, ...entry.entries];
    } else {
      combinedEntries.push({ ...entry, entries: [...entry.entries] });
    }
  });

  return combinedEntries;
};

/**
 * Util to check which of the given users can be planned on the planning board
 *
 * @param userList The complete user list
 * @returns Only operational available users
 */
export const filterUserForGChecks = (userList: SchambeckUser[]): SchambeckUser[] => {
  return userList.filter((user) => {
    if (!user.contractInformation.eyeTestDate) return true;
    if (dayjs().isBefore(user.contractInformation.eyeTestDate)) return true;
    if (
      dayjs()
        .subtract(user.contractInformation.eyeTestDeadline || 0, 'months')
        .isBefore(user.contractInformation.eyeTestDate)
    )
      return true;
    return false;
  });
};

/**
 * Util method to convert a simple user to tableRows that show the workExpirience at a specific customer
 * @param users array of simpleUsers to convert
 * @param customerId to get the times for
 * @returns  Array of TableRows containing the expirience
 */
export const convertSimpleUsersToTimeAtCustomerTableRows = (
  users: SimpleUser[],
  customerId: string,
): TableRow[] => {
  const relevantUsers: SimpleUser[] = users.filter((user) => !!user.customerTimes.get(customerId));
  return relevantUsers.map((user) => ({
    id: user.id,
    content: [getUserName(user), (user.customerTimes.get(customerId)! / 60).toFixed(1)],
  }));
};

/**
 * Util to determine on which date the user propation ends
 *
 * @param entryDate The user entered date on which the employee starts
 * @returns The end date of the propation (+6 Months)
 * @tested
 */
export const updatePropationEnd = (entryDate?: Date): Date | undefined => {
  if (!entryDate || !dayjs(entryDate).isValid()) return;
  return dayjs(entryDate).add(6, 'months').toDate();
};

/**
 * Util method to check if a user should see confidental information about another user
 * @param readUser the user that would see the data
 * @param dataUser the user to read the data from
 * @param offices list of offices to check for the hr role
 * @returns boolean if the user should be able to see the data
 * @tested
 */
export const isUserAllowedToSeeConfidentialInformations = (
  readUser: SchambeckUser,
  dataUser: SchambeckUser,
  offices: Office[],
): boolean => {
  const isAdmin: boolean =
    readUser.right.customRights.get(Right.ALL) || readUser.right.role.rights.includes(Right.ALL);
  return (
    (readUser.userFunctionId === dataUser.supervisorId &&
      dataUser.contractInformation.officeId === readUser.contractInformation.officeId) ||
    offices
      .filter(
        (office) =>
          office.id === dataUser.officeId ||
          office.id === dataUser.contractInformation.currentOfficeId,
      )
      .some((office) => office.hrEmployees.includes(readUser.id)) ||
    isAdmin
  );
};

/**
 * Util method to create TableRows for contractChanges
 * @param entries all changes to display
 * @param userFunctions Array of userFunctions to display the corrosponding function for each entry
 * @param companies list off all companies
 * @param offices list of all offices
 * @param language the language to display the rows
 * @returns Array of TableRows for contractChanges
 */
export const getContractHistoryRows = (
  entries: EmploymentHistoryEntry[],
  userFunctions: UserFunction[],
  companies: Company[],
  offices: Office[],
  rowToEdit: string | undefined,
  setRowToEdit: (id: string | undefined) => void,
  setRowToDelete: (id: string | undefined) => void,
  updateEntry: (newEntry: EmploymentHistoryEntry) => void,
): TableRow[] => {
  return entries.map((entry) => {
    const isDisabled: boolean = rowToEdit !== entry.id;
    return {
      id: entry.id,
      actions: [
        rowToEdit === entry.id ? (
          <IconComponent type={IconType.SAVE} width={30} onClick={() => setRowToEdit(undefined)} />
        ) : (
          <IconComponent type={IconType.EDIT} width={30} onClick={() => setRowToEdit(entry.id)} />
        ),
        <IconComponent
          type={IconType.DELETE}
          width={30}
          onClick={() => setRowToDelete(entry.id)}
          className="delete"
        />,
      ],
      content: [
        <Input
          type="date"
          value={entry.entryDate}
          onChangeDate={(entryDate) => entryDate && updateEntry({ ...entry, entryDate })}
          disabled={isDisabled}
        />,
        <Input
          type="date"
          value={entry.leavingDate}
          onChangeDate={(leavingDate) => leavingDate && updateEntry({ ...entry, leavingDate })}
          disabled={isDisabled}
        />,
        <Dropdown
          options={generateDropdownOptions(userFunctions, 'title', 'id')}
          selectedOption={entry.userFunctionId}
          disabled={isDisabled}
          onChange={(userFunctionId) => updateEntry({ ...entry, userFunctionId })}
        />,
        <Input
          disabled={isDisabled}
          type="number"
          value={entry.weeklyWorkTime}
          onChangeNumber={(weeklyWorkTime) =>
            weeklyWorkTime && updateEntry({ ...entry, weeklyWorkTime })
          }
        />,
        <Input
          disabled={isDisabled}
          type="number"
          value={entry.salary}
          onChangeNumber={(salary) => salary && updateEntry({ ...entry, salary })}
        />,
        <Input
          disabled={isDisabled}
          type="number"
          value={entry.vacationDays}
          onChangeNumber={(vacationDays) => vacationDays && updateEntry({ ...entry, vacationDays })}
        />,
        <Dropdown
          options={generateDropdownOptions(companies, 'name', 'id')}
          selectedOption={entry.companyId}
          disabled={isDisabled}
          onChange={(companyId) => updateEntry({ ...entry, companyId })}
        />,
        <Dropdown
          options={generateDropdownOptions(offices, 'name', 'id')}
          selectedOption={entry.officeId}
          disabled={isDisabled}
          onChange={(officeId) => updateEntry({ ...entry, officeId })}
        />,
      ],
    };
  });
};

/**
 * Util method to create an emoty AzkReduction
 * @param createdBy required information for a reduction
 * @param override partial reduction to adjust created instance
 * @returns  created azkReduction
 */
export const generateEmptyAzkReduction = (
  createdBy: string,
  override?: Partial<AzkReduction>,
): AzkReduction => ({
  createdBy,
  id: undefined!,
  amount: 0,
  comment: '',
  createDate: new Date(),
  effectiveAt: new Date(),
  type: AzkReductionType.PAYOUT,
  ...override,
});

/**
 * Util method to check if a user is the hrEmployee for the office of another user
 * @param responsibleUser the user to check if a action is available
 * @param user the user where the actions should be performed
 * @param offices list of all offices to check if the responsibleUser is in the hrEmployees
 * @returns boolean for true if the user can perform the action or not
 */
export const isResponsibleHREmployee = (
  responsibleUser: SchambeckUser,
  user: SchambeckUser,
  offices: Office[],
): boolean => {
  return (
    offices
      .find((office) => office.id === user.contractInformation.currentOfficeId)
      ?.hrEmployees.includes(responsibleUser.id) || false
  );
};

/**
 * Util method to add or substract vacationDays for a user
 * @param user the user to change the available holidays
 * @param vacationChange the actual change to make
 * @returns the updated user instance
 * @tested
 */
export const addVacationChange = (
  user: SchambeckUser,
  vacationChange: VacationChange,
): SchambeckUser => {
  const yearlyTimes: Map<number, YearlyTimeOverview> = updateYearlyTimesForVacationChange(
    user,
    vacationChange,
  );
  const vacationChanges: VacationChange[] = [...user.vacationChanges, vacationChange];
  const isAdd: boolean = vacationChange.type === AdjustmentType.ADD;

  const availableVacationDays: number = isAdd
    ? user.availableVacationDays + vacationChange.amount
    : user.availableVacationDays - vacationChange.amount;
  const updatedUser: SchambeckUser = {
    ...user,
    vacationChanges,
    availableVacationDays,
    contractInformation: { ...user.contractInformation, yearlyTimes },
  };
  return updatedUser;
};

/**
 * Util method to update times for a vacation change
 * @param user the user tp update
 * @param vacationChange the change to calculate
 * @returns new yearly times
 */
export const updateYearlyTimesForVacationChange = (
  user: SchambeckUser,
  vacationChange: VacationChange,
): Map<number, YearlyTimeOverview> => {
  const isAdd: boolean = vacationChange.type === AdjustmentType.ADD;
  const yearlyTimes: Map<number, YearlyTimeOverview> = new Map(
    user.contractInformation.yearlyTimes,
  );
  const effectedYear: number = vacationChange.effectiveAt.getFullYear();
  const effectedMonth: number = vacationChange.effectiveAt.getMonth() + 1;
  const dateNow: Date = new Date();
  //Adjust all years since change is effective
  for (let currYear = effectedYear; currYear <= dateNow.getFullYear(); currYear = currYear + 1) {
    let timeOverview: YearlyTimeOverview | undefined = yearlyTimes.get(currYear);
    if (!timeOverview)
      timeOverview = {
        availableVacationDays: new Map<number, number>(),
        confirmations: [],
        overTimeSaldos: [],
        totalIst: 0,
        totalSick: 0,
        totalSoll: 0,
      };

    const maxMonth: number = 12;
    //update month and following months
    for (let currMonth = 1; currMonth <= maxMonth; currMonth = currMonth + 1) {
      const monthPresent: boolean = !!timeOverview.availableVacationDays.get(currMonth);
      const shouldBeSkipped: boolean =
        currMonth > effectedMonth && currYear >= effectedYear && !monthPresent;
      //Month is not created and in the future, do not create entry
      if (shouldBeSkipped) continue;
      let prev: number = 0;
      if (monthPresent) prev = timeOverview?.availableVacationDays.get(currMonth) || 0;
      else {
        const isJan: boolean = currMonth === 1;
        const prevMonth: number = isJan ? 12 : currMonth - 1;
        const prevYear: number = isJan ? currYear - 1 : currYear;
        prev = yearlyTimes.get(prevYear)?.availableVacationDays.get(prevMonth) || 0;
      }
      const isEffected: boolean =
        currYear > effectedYear || (currYear === effectedYear && currMonth >= effectedMonth);
      if (currYear === dateNow.getFullYear() && currMonth > dateNow.getMonth() + 1) {
        continue;
      }
      if (isEffected) {
        timeOverview.availableVacationDays.set(
          currMonth,
          isAdd ? prev + vacationChange.amount : prev - vacationChange.amount,
        );
      } else {
        timeOverview.availableVacationDays.set(currMonth, prev);
      }
    }
    yearlyTimes?.set(currYear, timeOverview);
  }
  return yearlyTimes;
};

/**
 * Util method to remove a vacation change from a user
 * @param user the user to remove the change
 * @param change the change to remove
 * @returns updated user instance
 */
export const removeVacationChange = (
  user: SchambeckUser,
  change: VacationChange,
): SchambeckUser => {
  const revertedChange: VacationChange = {
    ...change,
    type: change.type === AdjustmentType.ADD ? AdjustmentType.SUBTRACT : AdjustmentType.ADD,
  };
  const isAdd: boolean = revertedChange.type === AdjustmentType.ADD;

  const yearlyTimes: Map<number, YearlyTimeOverview> = updateYearlyTimesForVacationChange(
    user,
    revertedChange,
  );
  const availableVacationDays: number = isAdd
    ? user.availableVacationDays + revertedChange.amount
    : user.availableVacationDays - revertedChange.amount;
  const vacationChanges: VacationChange[] = user.vacationChanges.filter(
    (vacChange) => vacChange.id !== change.id,
  );

  const updatedUser: SchambeckUser = {
    ...user,
    vacationChanges,
    availableVacationDays,
    contractInformation: { ...user.contractInformation, yearlyTimes },
  };
  return updatedUser;
};

/**
 * Util method to add a azkReduction to a user
 * @param user the user tp add the reduction to
 * @param change the azkReduction to add
 * @returns updated User object
 * @tested
 */
export const addOvertimeChange = (
  user: SchambeckUser,
  change: AzkReduction,
  isDelete: boolean,
): SchambeckUser => {
  const dateNow: Date = new Date();
  if (isDelete)
    change = {
      ...change,
      type:
        change.type === AzkReductionType.CORRECTION_ADD
          ? AzkReductionType.CORRECTION_SUBTRACT
          : AzkReductionType.CORRECTION_ADD,
    };
  change = { ...change, effectiveAt: new Date(change.effectiveAt) };
  const amount: number =
    change.type === AzkReductionType.CORRECTION_ADD ? change.amount : -change.amount;
  const effectiveYear: number = change.effectiveAt.getFullYear();
  const effectiveMonth: number = change.effectiveAt.getMonth() + 1;
  const currentYear: number = dateNow.getFullYear();
  const currentMonth: number = dateNow.getMonth() + 1;
  //Adjust effected year and following
  for (let currYear = effectiveYear; currYear <= currentYear; currYear++) {
    let currYearTimes: YearlyTimeOverview | undefined =
      user.contractInformation.yearlyTimes?.get(currYear);

    if (!currYearTimes)
      currYearTimes = {
        availableVacationDays: new Map<number, number>(),
        confirmations: [],
        overTimeSaldos: [],
        totalIst: 0,
        totalSick: 0,
        totalSoll: 0,
      };
    const minMonth: number = currYear === effectiveYear ? effectiveMonth : 1;
    const maxMonth: number = currYear === currentYear ? currentMonth : 12;

    for (let month = minMonth; month <= maxMonth; month++) {
      currYearTimes.overTimeSaldos = currYearTimes.overTimeSaldos.map((saldo) => {
        if (saldo.month !== month) return saldo;
        const start: number = saldo.overTimeStart;

        const oldChange: AzkReduction | undefined = saldo.azkReductions.find(
          (reduction) => reduction.id === change.id,
        );
        let oldAmount: number = oldChange && !isDelete ? oldChange.amount : 0;
        if (oldChange?.type !== AzkReductionType.CORRECTION_ADD) oldAmount = oldAmount * -1;

        const changeAmount: number = amount - oldAmount;
        let reductions: AzkReduction[] = [...saldo.azkReductions];
        if (effectiveMonth === month && effectiveYear === currYear) {
          if (isDelete)
            reductions = saldo.azkReductions.filter((reduction) => reduction.id !== change.id);
          else [...saldo.azkReductions.filter((reduction) => reduction.id !== change.id), change];
        }
        return {
          ...saldo,
          azkReductions: reductions,
          overTimeEnd: saldo.overTimeEnd + changeAmount,
          overTimeStart:
            month === effectiveMonth && currYear === effectiveYear ? start : start + changeAmount,
        };
      });
    }
    //Effected month is not present in the overtimes, so it is created
    const monthPresent: boolean = currYearTimes.overTimeSaldos.some(
      (saldo) => saldo.month === effectiveMonth,
    );
    if (!monthPresent && currYear === effectiveYear) {
      const isJanuary: boolean = effectiveMonth === 1;
      let startTime: number = 0;
      let endTime: number = 0;

      if (isJanuary) {
        const previous: MonthlyOvertime | undefined = user.contractInformation.yearlyTimes
          ?.get(currYear - 1)
          ?.overTimeSaldos.find((saldo) => saldo.month === 12);
        startTime = previous?.overTimeEnd || 0;
        endTime = (previous?.overTimeEnd || 0) + amount;
      } else {
        const previous: MonthlyOvertime | undefined = currYearTimes.overTimeSaldos.find(
          (saldo) => saldo.month === effectiveMonth - 1,
        );
        startTime = previous?.overTimeEnd || 0;
        endTime = (previous?.overTimeEnd || 0) + amount;
      }
      currYearTimes.overTimeSaldos.push({
        azkReductions: [change],
        id: uid(),
        month: effectiveMonth,
        overTimeEnd: endTime,
        overTimeStart: startTime,
      });
    }
    user.contractInformation.yearlyTimes!.set(currYear, currYearTimes);
  }
  return user;
};

/**
 * Util method to get all atkReductions for a user
 * @param user the user to get the entries for
 * @returns Array of all found reductions
 */
export const getAllAzkReductionsForUser = (user: SchambeckUser): AzkReduction[] => {
  const reductions: AzkReduction[] = [];
  user.contractInformation.yearlyTimes?.forEach((yearlyTime) => {
    yearlyTime.overTimeSaldos.forEach((saldo) => reductions.push(...saldo.azkReductions));
  });
  return reductions;
};

/**
 * Helper method to generate an empty monthlyOvertime
 * @param override partial overtime to adjust created entity
 * @returns created monthlyOvertime
 */
export const generateEmptyOverTime = (override?: Partial<MonthlyOvertime>): MonthlyOvertime => ({
  azkReductions: [],
  id: undefined!,
  month: 0,
  overTimeStart: 0,
  overTimeEnd: 0,
  ...override,
});

/**
 * Util method to get the latest entry for the users overtime saldo
 * @param user the entity to load the overtime
 * @returns latest overtime or undefined if no entry exists
 * @tested
 */
export const getLastOvertimeForUser = (user: SchambeckUser): MonthlyOvertime | undefined => {
  const years: number[] = Array.from(user.contractInformation.yearlyTimes?.keys() || []).sort(
    (yearA, yearB) => yearB - yearA,
  );

  for (const year of years) {
    const yearData: YearlyTimeOverview | undefined =
      user.contractInformation.yearlyTimes?.get(year);
    if (!yearData) continue;

    // Try to find December first, then any month
    for (let month = 12; month >= 1; month--) {
      const monthData: MonthlyOvertime | undefined = yearData.overTimeSaldos.find(
        (saldo) => saldo.month === month,
      );
      if (monthData) return monthData;
    }
  }
};
